<template>
  <div class="p-4" :class="{ 'removing-section': removingSection }">
    <div class="section-header">
      <Button class="link back-button" @click="$emit('close-section')">
        <v-icon name="chevron-left" scale="0.7" />
        <span class="ml-1">
          Back
        </span>
      </Button>
      <p class="section-name">
        {{ sectionName }}
      </p>

      <button
        v-if="section && !section.locked && !$apollo.loading"
        class="section-remove"
        @click="onRemoveClick"
      >
        Delete
      </button>
    </div>

    <Loader v-if="$apollo.loading || removingSection" :label="loaderLabel" />

    <div v-if="!$apollo.loading && !removingSection" class="section-settings">
      <div v-if="section && section.settings">
        <component
          :is="setting.__typename"
          v-for="(setting, index) in section.settings"
          :key="`${section.sectionId}-${index}`"
          :setting="setting"
          :saving="savingSection"
          @setting-updated="onSettingUpdated"
        >
          <slot />
        </component>
      </div>

      <div v-else class="section-settings">
        <p class="mb-0 text-grey">
          No settings found
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import compare from "just-compare";
import Loader from "@/components/UI/Loader";
import { Campaign, Section } from "@/graphql/queries";
import {
  RemoveSectionFromCampaign,
  UpdateSectionSettings,
} from "@/graphql/mutations";
import EventBus from "@/lib/event-bus.js";
import SettingTypes from "@/components/Settings/settingTypes";

export default {
  name: "SectionSettings",
  apollo: {
    section: {
      query: Section,
      update: (data) => data.section,
      variables() {
        return {
          input: {
            sectionId: this.sectionId,
            campaignId: this.campaignId,
          },
        };
      },
      result({ loading }) {
        if (!loading) this.setupSettings();
      },
    },
  },
  components: { Loader, ...SettingTypes },
  props: {
    campaignId: {
      required: true,
      type: String,
    },
    sectionName: {
      required: true,
      type: String,
    },
    sectionId: {
      required: true,
      type: String,
    },
  },
  data: () => ({
    removingSection: false,
    savingSection: false,
    section: {},
    settings: [],
  }),
  computed: {
    loaderLabel() {
      if (this.removingSection) return "Removing Section...";

      return "Loading Section...";
    },
  },
  methods: {
    formatedSettingsForMutation() {
      return this.settings.map((setting) => ({
        settingId: setting.id,
        value: setting[setting.valueField],
      }));
    },
    onRemoveClick() {
      // Show an alert to warn the user...
      this.$modal.show("dialog", {
        title: "📛 Confirm Deletion 📛",
        text:
          "This will remove the section and it's settings. Are you sure you want to continue?",
        buttons: [
          {
            title: "Yes",
            class: "vue-dialog-button bg-danger hover:bg-brand",
            handler: () => {
              this.$modal.hide("dialog");
              this.removeSection();
            },
          },
          {
            title: "Cancel",
            class: "vue-dialog-button bg-support-light hover:bg-support",
          },
        ],
      });
    },
    async removeSection() {
      this.$gtm.trackEvent({
        event: "promo-section-remove",
        sectionName: this.sectionName,
        sectionId: this.sectionId,
        campaignId: this.campaignId,
      });

      this.removingSection = true;

      // Emit the preview loading event to put the Preview component in it's loading state...
      EventBus.$emit("preview-loading", true);

      try {
        // Send the mutation
        await this.$apollo.mutate({
          mutation: RemoveSectionFromCampaign,
          variables: {
            input: {
              campaignId: this.campaignId,
              sectionId: this.sectionId,
            },
          },
          // Update the cache with the result
          // The query will be updated with the optimistic response
          // and then with the real result of the mutation
          update: (store, { data: { removeSectionFromCampaign } }) => {
            this.$emit("remove-section");

            EventBus.$emit("refetch-preview");

            this.updateCampaign(store, removeSectionFromCampaign);
          },
        });
      } catch (error) {
        this.removingSection = false;

        this.$modal.show("dialog", {
          title: "Oh no 😯",
          text: `Sorry we couldn't remove the section. Please try again. If the problem persists please contact our <a href="mailto:support@promo.ai?subject=Promo.ai Error: Remove Section&body=%0D%0A%0D%0AError: ${error}">support team</a>.`,
          buttons: [
            {
              title: "Close",
            },
          ],
        });
      }
    },
    async saveSection() {
      this.$gtm.trackEvent({
        event: "promo-section-save",
        sectionName: this.sectionName,
        sectionId: this.sectionId,
        campaignId: this.campaignId,
      });

      this.savingSection = true;

      // Emit the preview loading event to put the Preview component in it's loading state...
      EventBus.$emit("preview-loading", true);

      try {
        // Send the mutation
        await this.$apollo.mutate({
          mutation: UpdateSectionSettings,
          variables: {
            input: {
              campaignId: this.campaignId,
              sectionId: this.sectionId,
              settings: this.formatedSettingsForMutation(),
            },
          },
          // Update the cache with the result
          // The query will be updated with the optimistic response
          // and then with the real result of the mutation
          update: (store, { data: { updateSectionSettings } }) => {
            this.updateSection(store, updateSectionSettings);

            EventBus.$emit("refetch-preview");
          },
        });

        this.savingSection = false;
      } catch (error) {
        this.savingSection = false;

        this.$modal.show("dialog", {
          title: "Oh no 😯",
          text: `Sorry we couldn't update the section. You'll need to change the value of the field you're trying to update before trying again with the desired value. If the problem persists please contact our <a href="mailto:support@promo.ai?subject=Promo.ai Error: Save Section&body=%0D%0A%0D%0AError: ${error}">support team</a>.`,
          buttons: [
            {
              title: "Close",
            },
          ],
        });
      }
    },
    onSettingUpdated(updatedSetting, value) {
      // Try find the index of an existing setting
      const settingsIndex = this.settings.findIndex(
        (s) => s.id === updatedSetting.id
      );

      // If the existing setting has changed then...
      if (!compare(this.settings[settingsIndex], updatedSetting)) {
        // Update the existing setting with the updatedSetting...
        this.settings[settingsIndex] = updatedSetting;

        // And save the section...
        this.saveSection();
      }
    },
    setupSettings() {
      this.settings = this.section?.settings;
    },
    updateCampaign(store, response) {
      const campaignInput = {
        input: {
          campaignId: this.campaignId,
        },
      };

      // Read the data from our cache for this query.
      const data = store.readQuery({
        query: Campaign,
        variables: campaignInput,
      });

      // Update the cache with the response
      data.campaign.template.sections = response.template?.sections;

      // Write our data back to the cache.
      store.writeQuery({
        query: Campaign,
        variables: campaignInput,
        data,
      });
    },
    updateSection(store, response) {
      const sectionInput = {
        input: {
          sectionId: this.sectionId,
          campaignId: this.campaignId,
        },
      };

      // Read the data from our cache for this query.
      const data = store.readQuery({
        query: Section,
        variables: sectionInput,
      });

      // Update the cache with the response
      data.section = response;

      // Write our data back to the cache.
      store.writeQuery({
        query: Section,
        variables: sectionInput,
        data,
      });
    },
  },
};
</script>

<style scoped>
.section-header {
  @apply flex items-center border-grey-light bg-brand-light border rounded-lg shadow mb-4 overflow-hidden;
}

.section-name {
  @apply m-0 text-support text-sm font-bold pl-2;
}

.back-button {
  @apply flex items-center relative py-4 mr-2 rounded-none border-r border-grey-light border-solid text-sm;
  background: #f1f3fd;

  &:hover {
    @apply text-brand no-underline;
  }
}

.removing-section {
  @apply cursor-not-allowed pointer-events-none;
  opacity: 0.6;
}

.section-remove {
  @apply text-danger-dark ml-auto text-xs mr-3 outline-none;
}
</style>
